/**
 * View Tracker Component
 * Invisible component that tracks page views for:
 * 1. Analytics (all users) - stored in ANALYTICS_KV
 * 2. Recently viewed history (authenticated users only)
 */

'use client';

import { useEffect, useRef } from 'react';
import { useAuth } from '@/lib/auth/AuthContext';

interface ViewTrackerProps {
  contentType: string;
  contentSlug: string;
  contentTitle?: string;
}

/**
 * Map content type to analytics event name
 */
function getEventName(contentType: string): string {
  switch (contentType) {
    case 'insight':
    case 'insights':
      return 'insight_view';
    case 'framework':
    case 'frameworks':
      return 'framework_view';
    case 'article':
    case 'blog':
      return 'article_view';
    default:
      return 'page_view';
  }
}

export default function ViewTracker({
  contentType,
  contentSlug,
  contentTitle,
}: ViewTrackerProps) {
  const { isAuthenticated } = useAuth();
  const hasTrackedAnalytics = useRef(false);
  const hasTrackedRecent = useRef(false);

  // Track analytics for ALL users (anonymous and authenticated)
  useEffect(() => {
    if (hasTrackedAnalytics.current) return;

    const timeoutId = setTimeout(async () => {
      if (hasTrackedAnalytics.current) return;
      hasTrackedAnalytics.current = true;

      try {
        await fetch('/api/public/analytics', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: getEventName(contentType),
            page: window.location.pathname,
            properties: {
              contentType,
              contentSlug,
              contentTitle: contentTitle || '',
            },
          }),
        });
      } catch (error) {
        console.error('Failed to track analytics:', error);
        hasTrackedAnalytics.current = false;
      }
    }, 500); // Short delay to avoid tracking quick navigations

    return () => clearTimeout(timeoutId);
  }, [contentType, contentSlug, contentTitle]);

  // Track recently viewed for authenticated users only
  useEffect(() => {
    if (!isAuthenticated || hasTrackedRecent.current) return;

    const timeoutId = setTimeout(async () => {
      if (hasTrackedRecent.current) return;
      hasTrackedRecent.current = true;

      try {
        await fetch('/api/secure/recently-viewed', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            contentType,
            contentSlug,
            contentTitle,
          }),
        });
      } catch (error) {
        console.error('Failed to track view:', error);
        hasTrackedRecent.current = false;
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, contentType, contentSlug, contentTitle]);

  return null;
}
